import {
  getCurrentInstance,
  getCurrentScope,
  nextTick,
  onActivated,
  onDeactivated,
  onScopeDispose,
  ref,
  Ref,
  watch,
} from 'vue';
import { Router, useRouter } from 'vue-router';

interface PendingUpdate {
  router: Router;
  path: string;
  windowPath: string;
  changes: Record<string, string>;
  push: boolean;
  done: Promise<void>;
}

let pending: PendingUpdate | null = null;
let lastNavigation: Promise<unknown> = Promise.resolve();

// The value each key is about to have in the URL, from the queued or in-flight
// navigation that last changed it. The URL lags behind by a few microtasks, so
// a change is compared against this value, not against the URL.
const intended = new Map<string, { value: string; batch: PendingUpdate }>();

const getUrlParams = () => {
  if (typeof window !== 'undefined') {
    return new URLSearchParams(window.location.search);
  }
  return new URLSearchParams();
};

const getQueryStringVal = (key: string): string | null =>
  getUrlParams().get(key);

// The URL changes through the router so vue-router keeps its own history
// state; writing window.history directly broke Back and later navigation.
// Changes made in the same tick become one navigation, and navigations run one
// after another so each starts from the query the previous one produced.
const scheduleUpdate = (
  router: Router,
  key: string,
  value: string,
  replace: boolean
): Promise<void> => {
  if (!pending) {
    const batch = {
      router,
      path: router.currentRoute.value.path,
      windowPath: window.location.pathname,
      changes: {},
      push: false,
    } as PendingUpdate;
    pending = batch;
    batch.done = lastNavigation.then(async () => {
      if (pending === batch) {
        pending = null;
      }
      const route = batch.router.currentRoute.value;
      // The user left the page before the change was applied. The browser
      // URL moves first on Back, before the router commits the route.
      if (
        route.path !== batch.path ||
        window.location.pathname !== batch.windowPath
      ) {
        return;
      }
      const query = { ...route.query };
      Object.entries(batch.changes).forEach(([changedKey, changedValue]) => {
        if (changedValue.trim() !== '') {
          query[changedKey] = changedValue;
        } else {
          delete query[changedKey];
        }
      });
      const location = { path: route.path, query, hash: route.hash };
      await (batch.push
        ? batch.router.push(location)
        : batch.router.replace(location));
    });
    lastNavigation = batch.done.catch(() => {});
    const settle = () => {
      Object.keys(batch.changes).forEach(changedKey => {
        if (intended.get(changedKey)?.batch === batch) {
          intended.delete(changedKey);
        }
      });
    };
    batch.done.then(settle, settle);
  }
  pending.changes[key] = value;
  intended.set(key, { value, batch: pending });
  if (!replace) {
    pending.push = true;
  }
  return pending.done;
};

function useQueryParam(
  key: string,
  defaultVal: string,
  replace: boolean
): Ref<string>;
function useQueryParam(
  key: string,
  defaultVal: string,
  replace: boolean,
  asynchronous: true
): [() => string, (val: string) => Promise<void>];
function useQueryParam(
  key: string,
  defaultVal: string,
  replace: boolean,
  asynchronous?: true
): Ref<string> | [() => string, (val: string) => Promise<void>] {
  const router = useRouter();
  const readUrl = () => getQueryStringVal(key) || defaultVal;
  // The value the URL will show once queued navigations on this page land.
  const readIntendedUrl = () => {
    const next = intended.get(key);
    if (!next || next.batch.windowPath !== window.location.pathname) {
      return readUrl();
    }
    return next.value.trim() !== '' ? next.value : defaultVal;
  };
  const param = ref(readUrl());

  // The page this instance belongs to. On Back to another page the browser
  // fires popstate before vue-router commits the route, so the page that is
  // about to unmount still hears it. Taking that URL's values would let it
  // push itself back on top of the user's Back.
  let pagePath = window.location.pathname;
  const adoptUrl = () => {
    pagePath = window.location.pathname;
    param.value = readUrl();
  };

  // A page hidden by <keep-alive> stays alive but is not the page on screen,
  // so it neither reads nor writes the URL until it is shown again.
  let active = true;
  if (getCurrentInstance()) {
    onActivated(() => {
      active = true;
      adoptUrl();
    });
    onDeactivated(() => {
      active = false;
    });
  }
  const onOwnPage = () => active && window.location.pathname === pagePath;

  // An instance still mounted once the router has rendered a new path belongs
  // to that page now (a route param changed), so it follows the new URL. The
  // check waits for the render to finish: a page that <keep-alive> hides on
  // this navigation is only marked inactive at the end of it.
  watch(
    () => router.currentRoute.value.path,
    () =>
      nextTick(() => {
        if (active) {
          adoptUrl();
        }
      }),
    { flush: 'post' }
  );

  // Back and forward within the page.
  const onPopState = () => {
    if (onOwnPage()) {
      param.value = readUrl();
    }
  };

  // Keeps every instance using this key on the page in step with the one that
  // changed it.
  const onUpdate = (e: Event) => {
    if (onOwnPage()) {
      param.value = (e as CustomEvent).detail.newVal;
    }
  };

  window.addEventListener('popstate', onPopState);
  window.addEventListener(`update:${key}`, onUpdate);
  if (getCurrentScope()) {
    onScopeDispose(() => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener(`update:${key}`, onUpdate);
    });
  }

  const updateParam = (newVal: string) => {
    // The browser shows another page, or this one is hidden; the page on
    // screen owns the URL.
    if (!onOwnPage()) {
      return Promise.resolve();
    }
    window.dispatchEvent(
      new CustomEvent(`update:${key}`, {
        detail: { newVal: newVal || '' },
      })
    );
    return scheduleUpdate(router, key, newVal || '', replace);
  };

  if (!asynchronous) {
    watch(param, (newVal: string) => {
      if (readIntendedUrl() !== newVal) {
        updateParam(newVal);
      }
    });
  }

  if (asynchronous) {
    return [() => param.value, updateParam];
  }

  return param;
}

export default useQueryParam;
